<template>
  <div data-testid="rich-text-editor">
    <div :id="toolbarId" data-testid="rich-text-toolbar">
      <button class="ql-bold" aria-label="Bold" data-testid="rich-text-bold-btn"></button>
      <button class="ql-italic" aria-label="Italic" data-testid="rich-text-italic-btn"></button>
      <button
        class="ql-underline"
        aria-label="Underline"
        data-testid="rich-text-underline-btn"
      ></button>
      <button
        class="ql-list"
        value="ordered"
        aria-label="Numbered list"
        data-testid="rich-text-ordered-list-btn"
      ></button>
      <button
        class="ql-list"
        value="bullet"
        aria-label="Bullet list"
        data-testid="rich-text-bullet-list-btn"
      ></button>
      <button class="ql-link" aria-label="Link" data-testid="rich-text-link-btn"></button>
    </div>
    <div :id="editorId" data-testid="rich-text-editor-content"></div>
  </div>
</template>

<script>
import { editorOptions } from "@/shared_data/data";

// Shared (page-global) script tag id — Quill only ever needs to be loaded once per page,
// regardless of how many ContentRichTextEditor instances are mounted. Deliberately distinct
// from TextEditorV2.vue's "quill-script" id so the two components' load/cleanup lifecycles
// never interfere with each other (see quill-editor-migration-checklist.md Scope Items).
const SCRIPT_ID = "quill-script-content-rte";
const QUILL_SRC = "https://cdn.jsdelivr.net/npm/quill@1.3.6/dist/quill.js";

export default {
  name: "ContentRichTextEditor",
  props: {
    value: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      quill: null,
      // Per-instance DOM ids so multiple editors can co-exist on one page without collisions.
      toolbarId: `quill-toolbar-content-${this._uid}`,
      editorId: `quill-editor-content-${this._uid}`,
    };
  },
  mounted() {
    if (this.isQuillAvailable()) {
      this.$nextTick(() => this.configureQuill());
      return;
    }

    const existingScript = document.getElementById(SCRIPT_ID);
    if (existingScript) {
      // Another instance already started the load; just wait for it to finish.
      existingScript.addEventListener("load", () => {
        this.$nextTick(() => this.configureQuill());
      });
      return;
    }

    this.includeQuill(QUILL_SRC, () => {
      this.$nextTick(() => this.configureQuill());
    });
  },
  // NOTE: intentionally using beforeDestroy/destroyed, not beforeUnmount/unmounted.
  // Despite being the more common naming in this codebase, beforeUnmount/unmounted are
  // NOT invoked by Vue 2.7's Options API (only inside Composition API setup() via
  // onBeforeUnmount/onUnmounted) — verified via a failing cleanup test when renamed here.
  // Elsewhere in this codebase those names are silently dead no-op hooks; this component's
  // cleanup does real work (Quill listener teardown + shared script tag removal), so the
  // functionally-correct names must be used.
  beforeDestroy() {
    if (this.quill) {
      this.quill.off("text-change");
    }
  },
  destroyed() {
    const el = document.getElementById(SCRIPT_ID);
    if (el) el.parentNode.removeChild(el);
  },
  methods: {
    isQuillAvailable() {
      return typeof window.Quill !== "undefined";
    },
    includeQuill(url, callback) {
      const object = document.createElement("script");
      object.src = url;
      object.id = SCRIPT_ID;
      if (callback) {
        object.addEventListener("load", (e) => callback(null, e), false);
      }
      document.head.appendChild(object);
    },
    configureQuill() {
      try {
        // eslint-disable-next-line no-undef
        this.quill = new Quill(`#${this.editorId}`, {
          ...editorOptions,
          modules: { toolbar: `#${this.toolbarId}` },
          theme: "snow",
          placeholder: this.placeholder,
        });

        if (this.value) {
          this.quill.root.innerHTML = this.value;
        }

        if (this.disabled) {
          this.quill.disable();
        }

        this.quill.on("text-change", () => {
          this.$emit("input", this.getHTML());
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Error configuring ContentRichTextEditor's Quill instance", e);
      }
    },
    getHTML() {
      if (!this.quill) return null;
      const html = this.quill.root.innerHTML;
      // Contrarian review finding (2026-07-20): Quill's "empty" state isn't
      // always exactly "<p><br></p>" -- toggling the (now-enabled) list
      // buttons with no text typed produces "<ul><li><br></li></ul>" or
      // "<ol><li><br></li></ol>" instead. Without also treating these as
      // empty, an admin who clicks a list button and saves without typing
      // anything would persist a phantom empty bullet/number that then
      // renders on the participant-facing card. Verified against the real
      // installed quill package, not just this string comparison.
      const EMPTY_STATES = ["<p><br></p>", "<ul><li><br></li></ul>", "<ol><li><br></li></ol>"];
      return EMPTY_STATES.includes(html) ? null : html;
    },
    focusOnEditor() {
      if (this.quill) this.quill.focus();
    },
  },
};
</script>

<style scoped>
div[data-testid="rich-text-editor-content"] {
  min-height: 120px;
  border: 1px solid lightgray;
  border-radius: 8px;
  cursor: text;
}
</style>
